import { useEffect, useMemo, useState } from "react";
import {
  Button,
  cx,
  DatePicker,
  formatCanonical,
  Icon,
  Input,
  NumberInput,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Text,
  Title,
  Tooltip,
  useLocale,
  useToast,
} from "@app/ui";
import type { SelectOption } from "@app/ui";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { deleteLca, getLca, putLca } from "../api/dpp.js";
import type { LcaPutBody } from "../api/dpp.js";
import { useT } from "../i18n.js";
import {
  LCA_BOUNDARIES,
  LCA_CORE_SET,
  LCA_DATASETS,
  LCA_FUNCTIONAL_UNITS,
  LCA_HEADLINES,
  LCA_HEADLINE_SET,
  LCA_INDICATOR_GROUPS,
  LCA_INDICATORS,
  LCA_METHODS,
  LCA_MODULE_GROUPS,
  LCA_MODULES,
  LCA_UNITS,
  lcaIndicatorDef,
  unitLooksWrong,
} from "./lcaCatalog.js";

/** Mirrors `lcaPutSchema.indicators.max(40)` — the picker stops offering rows there. */
const MAX_INDICATORS = 40;

/** Group headers are non-selectable rows; this prefix keeps their values unique. */
const GROUP_PREFIX = "__group:";
/** Search text no user query can match, so headers vanish while filtering. */
const UNMATCHABLE = " ";

interface IndicatorDraft {
  /** Stable across reorders/deletes so React keeps each row's input state. */
  key: string;
  indicator: string;
  value: string;
  unit: string;
  module: string;
}

interface LcaDraft {
  source: "manual" | "epd" | "clca";
  externalRef: string;
  method: string;
  functionalUnit: string;
  systemBoundary: string;
  dataset: string;
  calculatedAt: string;
  verifiedBy: string;
  indicators: IndicatorDraft[];
}

let rowSeq = 0;
const newRow = (init?: Partial<Omit<IndicatorDraft, "key">>): IndicatorDraft => ({
  key: `r${rowSeq++}`,
  indicator: "",
  value: "",
  unit: "",
  module: "",
  ...init,
});

const EMPTY: LcaDraft = {
  source: "manual",
  externalRef: "",
  method: "",
  functionalUnit: "",
  systemBoundary: "",
  dataset: "",
  calculatedAt: "",
  verifiedBy: "",
  indicators: [],
};

/** Row keys are UI-only — the dirty check compares the payload, not the identity. */
function fingerprint(draft: LcaDraft): string {
  return JSON.stringify({
    ...draft,
    indicators: draft.indicators.map((r) => [r.indicator, r.value, r.unit, r.module]),
  });
}

const isComplete = (r: IndicatorDraft) =>
  r.indicator.trim() !== "" && r.value.trim() !== "" && r.unit.trim() !== "";

/**
 * A creatable Select only knows the options it is given, so a stored value that
 * is not a preset (an org's own method, a legacy unit) has to be injected or the
 * trigger would fall back to the placeholder and read as empty.
 */
function withCustom(options: SelectOption[], value: string): SelectOption[] {
  const v = value.trim();
  if (v === "" || options.some((o) => o.value === v)) return options;
  return [...options, { value: v, label: v }];
}

/**
 * LCA editor — headline metrics, the methodology declaration, and the indicator
 * table. Indicator codes, units and modules come from an EN 15804+A2 catalog
 * (`lcaCatalog.ts`) so a declaration is picked rather than typed, but every
 * picker stays creatable: the API takes free text and orgs declare indicators no
 * list covers.
 */
export function LcaCard({
  orgId,
  productId,
  canWrite,
}: {
  orgId: string;
  productId: string;
  canWrite: boolean;
}) {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const errorText = useErrorText();
  const [draft, setDraft] = useState<LcaDraft | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const lca = await getLca(orgId, productId);
        const next: LcaDraft = lca
          ? {
              source: lca.source,
              externalRef: lca.externalRef ?? "",
              method: lca.method ?? "",
              functionalUnit: lca.functionalUnit ?? "",
              systemBoundary: lca.systemBoundary ?? "",
              dataset: lca.dataset ?? "",
              calculatedAt: lca.calculatedAt ? lca.calculatedAt.slice(0, 10) : "",
              verifiedBy: lca.verifiedBy ?? "",
              indicators: lca.indicators.map((i) =>
                newRow({
                  indicator: i.indicator,
                  value: String(i.value),
                  unit: i.unit,
                  module: i.module ?? "",
                }),
              ),
            }
          : EMPTY;
        setExists(lca !== null);
        setDraft(next);
        setSaved(fingerprint(next));
      } catch (e) {
        setError(e);
      }
    })();
  }, [orgId, productId]);

  const rows = draft?.indicators ?? [];

  /* --- Option lists (rebuilt only when the language changes) ------------------ */

  const indicatorOptions = useMemo<SelectOption[]>(() => {
    const out: SelectOption[] = [];
    for (const group of LCA_INDICATOR_GROUPS) {
      out.push({
        value: `${GROUP_PREFIX}${group}`,
        label: t(`lcaGroup.${group}`),
        disabled: true,
        searchText: UNMATCHABLE,
      });
      for (const def of LCA_INDICATORS) {
        if (def.group !== group) continue;
        const name = t(`lcaInd.${def.code}`);
        out.push({
          value: def.code,
          label: (
            <span className="lca-opt">
              {name}
              <span className="lca-opt-code">{def.code}</span>
            </span>
          ),
          hint: def.unit,
          searchText: `${def.code} ${name} ${def.unit}`,
          depth: 1,
        });
      }
    }
    return out;
  }, [t]);

  const moduleOptions = useMemo<SelectOption[]>(() => {
    const out: SelectOption[] = [];
    for (const group of LCA_MODULE_GROUPS) {
      out.push({
        value: `${GROUP_PREFIX}${group}`,
        label: t(`lcaModGroup.${group}`),
        disabled: true,
        searchText: UNMATCHABLE,
      });
      for (const mod of LCA_MODULES) {
        if (mod.group !== group) continue;
        const name = t(`lcaMod.${mod.code}`);
        out.push({
          value: mod.code,
          label: mod.code,
          hint: name,
          searchText: `${mod.code} ${name}`,
          depth: 1,
        });
      }
    }
    return out;
  }, [t]);

  const unitOptions = useMemo<SelectOption[]>(() => LCA_UNITS.map((u) => ({ value: u, label: u })), []);
  const methodOptions = useMemo<SelectOption[]>(() => LCA_METHODS.map((m) => ({ value: m, label: m })), []);
  const datasetOptions = useMemo<SelectOption[]>(() => LCA_DATASETS.map((d) => ({ value: d, label: d })), []);
  const boundaryOptions = useMemo<SelectOption[]>(
    () => LCA_BOUNDARIES.map((b) => ({ value: b, label: t(`lcaBoundary.${b}`), hint: b, searchText: b })),
    [t],
  );
  const functionalUnitOptions = useMemo<SelectOption[]>(
    () => LCA_FUNCTIONAL_UNITS.map((k) => ({ value: t(`lcaFu.${k}`), label: t(`lcaFu.${k}`) })),
    [t],
  );

  /** The add picker greys out codes already declared instead of hiding them. */
  const addOptions = useMemo<SelectOption[]>(() => {
    const used = new Set(rows.map((r) => r.indicator.trim()));
    return indicatorOptions.map((o) =>
      used.has(o.value) ? { ...o, disabled: true, hint: t("lca.alreadyAdded") } : o,
    );
  }, [indicatorOptions, rows, t]);

  /* --- Derived state --------------------------------------------------------- */

  const duplicates = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const code = r.indicator.trim();
      if (code === "") continue;
      const key = `${code}|${r.module.trim()}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const headlines = useMemo(
    () =>
      LCA_HEADLINES.flatMap((h) => {
        for (const code of h.codes) {
          const row = rows.find((r) => r.indicator.trim() === code && r.value.trim() !== "");
          if (row) return [{ key: h.key, row }];
        }
        return [];
      }),
    [rows],
  );

  const validCount = rows.filter(isComplete).length;
  const incompleteCount = rows.length - validCount;
  const dirty = draft !== null && saved !== null && fingerprint(draft) !== saved;

  /* --- Mutations ------------------------------------------------------------- */

  const patch = (p: Partial<LcaDraft>) => setDraft((prev) => ({ ...prev!, ...p }));
  const patchRow = (key: string, p: Partial<IndicatorDraft>) =>
    setDraft((prev) => ({
      ...prev!,
      indicators: prev!.indicators.map((r) => (r.key === key ? { ...r, ...p } : r)),
    }));
  const removeRow = (key: string) =>
    setDraft((prev) => ({ ...prev!, indicators: prev!.indicators.filter((r) => r.key !== key) }));

  /** Appends codes that are not declared yet, stopping at the schema's cap. */
  const addCodes = (codes: readonly string[]) =>
    setDraft((prev) => {
      const p = prev!;
      const used = new Set(p.indicators.map((r) => r.indicator.trim()));
      const added: IndicatorDraft[] = [];
      for (const raw of codes) {
        const code = raw.trim();
        if (code === "" || code.startsWith(GROUP_PREFIX) || used.has(code)) continue;
        if (p.indicators.length + added.length >= MAX_INDICATORS) break;
        used.add(code);
        added.push(newRow({ indicator: code, unit: lcaIndicatorDef(code)?.unit ?? "" }));
      }
      if (added.length === 0) return p;
      return { ...p, indicators: [...p.indicators, ...added] };
    });

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const body: LcaPutBody = {
        source: draft.source,
        externalRef: draft.externalRef.trim() || null,
        method: draft.method.trim() || null,
        functionalUnit: draft.functionalUnit.trim() || null,
        systemBoundary: draft.systemBoundary.trim() || null,
        dataset: draft.dataset.trim() || null,
        calculatedAt: draft.calculatedAt || null,
        verifiedBy: draft.verifiedBy.trim() || null,
        indicators: draft.indicators.filter(isComplete).map((i) => ({
          indicator: i.indicator.trim(),
          value: Number(i.value),
          unit: i.unit.trim(),
          ...(i.module.trim() ? { module: i.module.trim() } : {}),
        })),
      };
      await putLca(orgId, productId, body);
      setExists(true);
      setSaved(fingerprint(draft));
      toast.success(t("lca.saved"));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await deleteLca(orgId, productId);
      setExists(false);
      setDraft(EMPTY);
      setSaved(fingerprint(EMPTY));
      toast.success(t("lca.deleted"));
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  if (error != null) return <ErrorAlert error={error} />;
  if (!draft) return <Spin />;

  const sourceLabel =
    draft.source === "manual"
      ? t("lca.sourceManual")
      : draft.source === "epd"
        ? t("lca.sourceEpd")
        : t("lca.sourceClca");

  /** Why a row is flagged, most blocking reason first; null when it is fine. */
  const rowWarning = (r: IndicatorDraft): string | null => {
    if (r.indicator.trim() !== "" && !isComplete(r)) return t("lca.rowIncomplete");
    const dup = duplicates.get(`${r.indicator.trim()}|${r.module.trim()}`) ?? 0;
    if (dup > 1) return t("lca.duplicate");
    if (unitLooksWrong(r.indicator, r.unit))
      return t("lca.unitMismatch", { unit: lcaIndicatorDef(r.indicator)!.unit });
    return null;
  };

  const addBar = (
    <div className="lca-add">
      <Select
        className="lca-add-select"
        placeholder={t("lca.indicatorPick")}
        options={addOptions}
        value=""
        creatable
        createLabel={(q) => t("lca.indicatorCustom", { q })}
        disabled={rows.length >= MAX_INDICATORS}
        onChange={(code) => addCodes([code])}
      />
      <Button
        size="sm"
        variant="subtle"
        icon={<Icon name="plus" size={14} />}
        disabled={rows.length >= MAX_INDICATORS}
        onClick={() => addCodes(LCA_HEADLINE_SET)}
      >
        {t("lca.addHeadlineSet")}
      </Button>
      <Button
        size="sm"
        variant="subtle"
        icon={<Icon name="plus" size={14} />}
        disabled={rows.length >= MAX_INDICATORS}
        onClick={() => addCodes(LCA_CORE_SET)}
      >
        {t("lca.addCoreSet")}
      </Button>
    </div>
  );

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      <Text secondary>{t("lca.hint")}</Text>

      {/* Headline metrics, read live off the draft — what the passport page leads with. */}
      {headlines.length > 0 && (
        <div className="lca-kpis">
          {headlines.map((h, i) => (
            <div key={h.key} className={cx("lca-kpi", i === 0 && "is-primary")}>
              <div className="lca-kpi-label">{t(`lcaHead.${h.key}`)}</div>
              <div className="lca-kpi-value">
                {formatCanonical(h.row.value, { locale })}
                <span>{h.row.unit}</span>
              </div>
              <div className="lca-kpi-meta">
                {h.row.indicator}
                {h.row.module.trim() ? ` · ${h.row.module.trim()}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Methodology — the declarations published beside the numbers. */}
      <section className="sc-section">
        <div className="sc-section-head">
          <Title level={5}>{t("lca.methodology")}</Title>
          <Tag color={draft.source === "manual" ? "default" : "primary"}>{sourceLabel}</Tag>
        </div>
        <Text small secondary>
          {t("lca.methodologyHint")}
        </Text>
        <div className="lca-meta-grid">
          <Select
            label={t("lca.source")}
            disabled={!canWrite}
            options={[
              { value: "manual", label: t("lca.sourceManual") },
              { value: "epd", label: t("lca.sourceEpd") },
              { value: "clca", label: t("lca.sourceClca") },
            ]}
            value={draft.source}
            onChange={(v) => patch({ source: v as LcaDraft["source"] })}
          />
          <Input
            label={t("lca.externalRef")}
            placeholder={t("lca.externalRefPh")}
            help={t("lca.externalRefHelp")}
            disabled={!canWrite}
            value={draft.externalRef}
            onChange={(e) => patch({ externalRef: e.target.value })}
          />
          <Select
            label={t("lca.method")}
            placeholder={t("lca.methodPh")}
            options={withCustom(methodOptions, draft.method)}
            value={draft.method}
            creatable
            allowClear
            disabled={!canWrite}
            onChange={(v) => patch({ method: v })}
          />
          <Select
            label={t("lca.functionalUnit")}
            placeholder={t("lca.functionalUnitPh")}
            options={withCustom(functionalUnitOptions, draft.functionalUnit)}
            value={draft.functionalUnit}
            creatable
            allowClear
            disabled={!canWrite}
            onChange={(v) => patch({ functionalUnit: v })}
          />
          <Select
            label={t("lca.systemBoundary")}
            placeholder={t("lca.systemBoundaryPh")}
            options={withCustom(boundaryOptions, draft.systemBoundary)}
            value={draft.systemBoundary}
            creatable
            allowClear
            disabled={!canWrite}
            onChange={(v) => patch({ systemBoundary: v })}
          />
          <Select
            label={t("lca.dataset")}
            placeholder={t("lca.datasetPh")}
            options={withCustom(datasetOptions, draft.dataset)}
            value={draft.dataset}
            creatable
            allowClear
            disabled={!canWrite}
            onChange={(v) => patch({ dataset: v })}
          />
          <DatePicker
            label={t("lca.calculatedAt")}
            allowClear
            disabled={!canWrite}
            value={draft.calculatedAt}
            onChange={(v) => patch({ calculatedAt: v })}
          />
          <Input
            label={t("lca.verifiedBy")}
            placeholder={t("lca.verifiedByPh")}
            disabled={!canWrite}
            value={draft.verifiedBy}
            onChange={(e) => patch({ verifiedBy: e.target.value })}
          />
        </div>
      </section>

      {/* Indicators — one compact row per declaration, headers written once. */}
      <section className="sc-section">
        <div className="sc-section-head">
          <Title level={5}>{t("lca.indicators")}</Title>
          {rows.length > 0 && <Tag>{t("lca.count", { n: rows.length, max: MAX_INDICATORS })}</Tag>}
        </div>
        <Text small secondary>
          {t("lca.indicatorsHint")}
        </Text>

        {rows.length === 0 ? (
          <div className="lca-empty">
            <Text strong>{t("lca.indicatorsEmpty")}</Text>
            <Text small secondary>
              {t("lca.indicatorsEmptyHint")}
            </Text>
            {canWrite && addBar}
          </div>
        ) : (
          <div className="lca-table-wrap">
            <div className="lca-table">
              <div className="lca-row lca-head">
                <span>{t("lca.indicator")}</span>
                <span className="lca-num-head">{t("lca.value")}</span>
                <span>{t("lca.unit")}</span>
                <span>{t("lca.module")}</span>
                <span />
              </div>
              {rows.map((r) => {
                const warning = rowWarning(r);
                return (
                  <div key={r.key} className="lca-row">
                    <Select
                      options={withCustom(indicatorOptions, r.indicator)}
                      value={r.indicator}
                      placeholder={t("lca.indicatorPh")}
                      creatable
                      disabled={!canWrite}
                      createLabel={(q) => t("lca.indicatorCustom", { q })}
                      onChange={(v) =>
                        patchRow(r.key, {
                          indicator: v,
                          // A freshly picked code brings its EN 15804 unit unless
                          // the row already carries one the user typed.
                          ...(r.unit.trim() === "" && lcaIndicatorDef(v)
                            ? { unit: lcaIndicatorDef(v)!.unit }
                            : {}),
                        })
                      }
                    />
                    <NumberInput
                      value={r.value}
                      disabled={!canWrite}
                      placeholder={t("lca.value")}
                      onChange={(v) => patchRow(r.key, { value: v })}
                    />
                    <Select
                      options={withCustom(unitOptions, r.unit)}
                      value={r.unit}
                      placeholder={t("lca.unit")}
                      creatable
                      disabled={!canWrite}
                      onChange={(v) => patchRow(r.key, { unit: v })}
                    />
                    <Select
                      options={withCustom(moduleOptions, r.module)}
                      value={r.module}
                      placeholder={t("lca.module")}
                      creatable
                      allowClear
                      disabled={!canWrite}
                      onChange={(v) => patchRow(r.key, { module: v })}
                    />
                    <span className="lca-row-actions">
                      {warning != null && (
                        <Tooltip title={warning}>
                          <span className="lca-warn" role="img" aria-label={warning}>
                            <Icon name="warning" size={14} />
                          </span>
                        </Tooltip>
                      )}
                      {canWrite && (
                        <Button
                          size="sm"
                          variant="subtle"
                          aria-label={t("common.remove")}
                          onClick={() => removeRow(r.key)}
                        >
                          <Icon name="trash" size={14} />
                        </Button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {rows.length > 0 && canWrite && addBar}
        {incompleteCount > 0 && (
          <Text small className="lca-note">
            {t("lca.incomplete", { n: incompleteCount })}
          </Text>
        )}
        {rows.length >= MAX_INDICATORS && (
          <Text small secondary>
            {t("lca.maxReached")}
          </Text>
        )}
      </section>

      {canWrite && (
        <div className="pd-savebar">
          <Text small secondary>
            {dirty ? t("lca.unsaved") : t("lca.publishCount", { n: validCount })}
          </Text>
          <Space>
            {exists && (
              <Popconfirm
                title={t("lca.deleteConfirm")}
                description={t("lca.deleteConfirmDesc")}
                okType="danger"
                okText={t("common.remove")}
                cancelText={t("common.cancel")}
                onConfirm={() => void remove()}
              >
                <Button variant="danger">{t("lca.delete")}</Button>
              </Popconfirm>
            )}
            <Button variant="primary" loading={saving} onClick={() => void save()}>
              {t("common.save")}
            </Button>
          </Space>
        </div>
      )}
    </Space>
  );
}
